import { OrganizationTreeProjectFooterRow } from '@/features/organization-project-picker/components/OrganizationTreeProjectFooterRow';
import { OrganizationTreeProjectStateRow } from '@/features/organization-project-picker/components/OrganizationTreeProjectStateRow';
import {
  organizationProjectIconVariants,
  organizationProjectLinkVariants,
  organizationProjectListVariants,
  organizationTreeChevronBoxVariants,
  organizationTreeChevronIconVariants,
  organizationTreeMarkVariants,
  organizationTreeNameVariants,
  organizationTreeRowVariants,
} from '@/features/organization-project-picker/config/organizationTree';
import type {
  OrganizationTreeProjectQuery,
  OrganizationTreeRow,
} from '@/features/organization-project-picker/types/organizationTree';
import { supervisorActivityScopeSearch } from '@/features/supervisor/lib/activityScopeSearch';
import { orgInitials } from '@/shared/lib/orgInitials';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, FolderOpen } from 'lucide-react';
import type { CSSProperties, Ref } from 'react';

/** Field activity tree row with activity-route-only URL updates. */
export function SupervisorActivityOrganizationTreeRow({
  row,
  activeOrganizationId,
  activeProjectId,
  withProjects,
  onExpandedChange,
  getProjectQuery,
  onRetryProjectBranch,
  measureRef,
  virtualIndex,
  style,
}: {
  row: OrganizationTreeRow;
  activeOrganizationId?: string;
  activeProjectId?: string;
  withProjects: boolean;
  onExpandedChange: (organizationId: string, expanded: boolean) => void;
  getProjectQuery: (organizationId: string) => OrganizationTreeProjectQuery | undefined;
  onRetryProjectBranch: (organizationId: string) => void;
  measureRef: Ref<HTMLLIElement>;
  virtualIndex: number;
  style: CSSProperties;
}) {
  const { t } = useLingui();

  if (row.kind === 'organization') {
    const { organizationId, name } = row.organization;
    const active = activeOrganizationId === organizationId && !activeProjectId;
    return (
      <li ref={measureRef} data-index={virtualIndex} style={style}>
        <div className={organizationTreeRowVariants({ active })}>
          {withProjects ? (
            <button
              type="button"
              aria-expanded={row.expanded}
              aria-label={row.expanded ? t`Collapse ${name}` : t`Expand ${name}`}
              onClick={() => onExpandedChange(organizationId, !row.expanded)}
              className={organizationTreeChevronBoxVariants()}
            >
              {row.expanded ? (
                <ChevronDown className={organizationTreeChevronIconVariants()} aria-hidden />
              ) : (
                <ChevronRight className={organizationTreeChevronIconVariants()} aria-hidden />
              )}
            </button>
          ) : (
            <span aria-hidden className={organizationTreeChevronBoxVariants()} />
          )}
          <Link
            from="/supervisor/team/field-activity"
            to="/supervisor/team/field-activity"
            search={(previous) => supervisorActivityScopeSearch(previous, organizationId)}
            replace
            aria-current={active ? 'page' : undefined}
            data-active={active}
            onClick={withProjects ? () => onExpandedChange(organizationId, true) : undefined}
            className="flex min-w-0 flex-1 items-center gap-1 self-stretch rounded-md outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
          >
            <span aria-hidden className={organizationTreeMarkVariants({ active })}>
              {orgInitials(name)}
            </span>
            <span className={organizationTreeNameVariants({ active })}>{name}</span>
          </Link>
        </div>
      </li>
    );
  }

  if (row.kind === 'project') {
    const active = activeProjectId === row.project.projectId;
    return (
      <li ref={measureRef} data-index={virtualIndex} style={style}>
        <div className={organizationProjectListVariants({ edge: row.edge })}>
          <Link
            from="/supervisor/team/field-activity"
            to="/supervisor/team/field-activity"
            search={(previous) =>
              supervisorActivityScopeSearch(previous, row.organizationId, row.project.projectId)
            }
            replace
            aria-current={active ? 'page' : undefined}
            data-active={active}
            className={organizationProjectLinkVariants()}
          >
            <FolderOpen className={organizationProjectIconVariants()} aria-hidden />
            <span className="min-w-0 flex-1 truncate">{row.project.name}</span>
          </Link>
        </div>
      </li>
    );
  }

  if (row.kind === 'project-state') {
    return (
      <OrganizationTreeProjectStateRow
        state={row.state}
        density="compact"
        edge={row.edge}
        onRetry={() => onRetryProjectBranch(row.organizationId)}
        measureRef={measureRef}
        virtualIndex={virtualIndex}
        style={style}
      />
    );
  }

  const projectQuery = getProjectQuery(row.organizationId);
  if (!projectQuery) return null;
  return (
    <OrganizationTreeProjectFooterRow
      density="compact"
      edge={row.edge}
      query={projectQuery}
      measureRef={measureRef}
      virtualIndex={virtualIndex}
      style={style}
    />
  );
}
